import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Card, DataTable, Modal, Badge, Link, Button, Filters, ButtonGroup, BlockStack, Pagination, Text, Tabs, Banner, InlineStack, Box, Select, Popover, ActionList } from '@shopify/polaris';
import { ViewIcon, ListBulletedIcon, PhoneIcon, EmailIcon, CalendarIcon } from '@shopify/polaris-icons';
import { getRequest, getTableFooter, postRequest } from '../../shared/components/functions';
import { useUrl } from '../../shared/util/hanldesave';
import { applyFilters, addFilters, handleFilterChange, removeFilter, handleFiltersClearAll } from '../../shared/components/listFilter.jsx';
import InteractionForm, { interactionTypes, interactionTypeGroups } from './components/InteractionForm';
import { useLookupStore, useAuthStore } from '../../context/useStore.js';
import ExcelExporterModal from '../../shared/components/modals/excelExporterModal.jsx';
import InteractionDocuments from '../interaction/components/InteractionDocuments';
import StatusManagementModal from './components/StatusManagementModal';
import StatusDropdown from './components/StatusDropdown';
import InteractionKanban from './components/InteractionKanban';

// Add a style tag to the document head to disable pointer events on the modal backdrop
const addModalBackdropStyle = () => {
  const styleId = 'modal-backdrop-style';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .Polaris-Modal-Backdrop {
        pointer-events: none !important;
      }
      .Polaris-Modal-Dialog {
        pointer-events: auto !important;
      }
    `;
    document.head.appendChild(style);
  }
};

/**
 * IMPORTANT NOTE FOR LLM AGENTS:
 * This page displays CUSTOMER interactions only. Interactions with an employee_id set
 * are NEVER listed here - they are filtered out in the backend query
 * (see: server/routes/raw_queries/interactions_query.js - WHERE employee_id IS NULL).
 * Employee interactions are displayed separately in the EmployeeTimeline component.
 */
export default function InteractionsPage() {
  const newInteraction = new URLSearchParams(window.location.search).get('new');
  const url = useUrl();
  const { users } = useLookupStore();
  const { user: currentUser } = useAuthStore();
  const navigate = useNavigate();

  // Tab state: 0 = My Interactions (logged-in user only), 1 = All Interactions
  const [listTab, setListTab] = useState(0);

  // View mode: 'table' or 'kanban' (only for My Interactions tab)
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('interactionsViewMode');
    return saved || 'table';
  });

  // Interaction statuses
  const [userStatuses, setUserStatuses] = useState([]);
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  // Follow-up filter
  const [followUpFilter, setFollowUpFilter] = useState(() => {
    return localStorage.getItem('interactionsFollowUpFilter') || '';
  });

  // Date range preset
  const [dateRangePreset, setDateRangePreset] = useState(() => {
    return localStorage.getItem('interactionsDateRangePreset') || '';
  });

  // Quick log popover
  const [quickLogPopoverActive, setQuickLogPopoverActive] = useState(false);

  // Follow-up reminder email state
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderBanner, setReminderBanner] = useState(null);

  const handleSendFollowUpReminders = async () => {
    setSendingReminders(true);
    setReminderBanner(null);
    try {
      const result = await postRequest(
        '/api/cron/trigger-follow-up-reminders',
        {},
        () => {},
        () => {}
      );
      if (result.success) {
        setReminderBanner({
          tone: 'success',
          message: result.emailsSent > 0 ? `${result.emailsSent} email(s) verzonden met ${result.totalFollowUps} follow-up(s)` : 'Geen follow-ups voor vandaag'
        });
      } else {
        setReminderBanner({ tone: 'critical', message: result.error || 'Er ging iets mis' });
      }
    } catch (error) {
      setReminderBanner({ tone: 'critical', message: error.message || 'Er ging iets mis' });
    }
    setSendingReminders(false);
  };

  const handleListTabChange = useCallback((selectedTabIndex) => {
    setIsTabSwitching(true);
    setListTab(selectedTabIndex);
    setPage(1); // Reset to first page when switching tabs
  }, []);

  const [interactions, setInteractions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [isTabSwitching, setIsTabSwitching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(newInteraction);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [modalTab, setModalTab] = useState(0);
  const [page, setPage] = useState(1);

  const handleModalTabChange = useCallback((selectedTabIndex) => {
    setModalTab(selectedTabIndex);
  }, []);
  const [filters, setFilters] = useState({
    q: { type: 'text', name: 'Q', value: url.get('q') || '' },
    company: { type: 'text', name: 'Company', value: url.get('company') || '' },
    contact: { type: 'text', name: 'Contact', value: url.get('contact') || '' },
    company_id: { type: 'text', name: 'Company ID', value: url.get('company_id') || '' },
    company_contact_id: { type: 'text', name: 'Contact ID', value: url.get('company_contact_id') || '' },
    type: {
      type: 'multiple',
      name: 'Type',
      value: url.get('type') ? JSON.parse(url.get('type')) : null,
      choices: interactionTypes
    },
    user_id: {
      type: 'multiple',
      name: 'User',
      value: url.get('user_id') ? JSON.parse(url.get('user_id')) : null,
      choices: users.map((user) => ({ label: user.Afkorting, value: user.GebruikerID }))
    },
    dateFrom: { type: 'date', name: 'Date from', value: url.get('dateFrom') || '' },
    dateTo: { type: 'date', name: 'Date to', value: url.get('dateTo') || '' }
  });

  // Add the style when the component mounts
  useEffect(() => {
    addModalBackdropStyle();
  }, []);

  // Fetch user statuses
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const data = await getRequest('/api/interaction-statuses');
        setUserStatuses(data);
      } catch (error) {
        console.error('Error fetching statuses:', error);
      }
    };
    fetchStatuses();
  }, []);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('interactionsViewMode', viewMode);
  }, [viewMode]);

  // Save follow-up filter preference
  useEffect(() => {
    localStorage.setItem('interactionsFollowUpFilter', followUpFilter);
  }, [followUpFilter]);

  // Save date range preset preference
  useEffect(() => {
    localStorage.setItem('interactionsDateRangePreset', dateRangePreset);
  }, [dateRangePreset]);

  // Apply date range preset to filters
  const applyDateRangePreset = useCallback(
    (preset) => {
      const today = new Date();
      let dateFrom = '';
      let dateTo = '';

      switch (preset) {
        case 'today':
          dateFrom = today.toISOString().split('T')[0];
          dateTo = today.toISOString().split('T')[0];
          break;
        case 'yesterday': {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          dateFrom = yesterday.toISOString().split('T')[0];
          dateTo = yesterday.toISOString().split('T')[0];
          break;
        }
        case 'last_7_days': {
          const last7 = new Date(today);
          last7.setDate(last7.getDate() - 7);
          dateFrom = last7.toISOString().split('T')[0];
          dateTo = today.toISOString().split('T')[0];
          break;
        }
        case 'last_30_days': {
          const last30 = new Date(today);
          last30.setDate(last30.getDate() - 30);
          dateFrom = last30.toISOString().split('T')[0];
          dateTo = today.toISOString().split('T')[0];
          break;
        }
        case 'this_month': {
          dateFrom = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
          dateTo = today.toISOString().split('T')[0];
          break;
        }
        case 'last_month': {
          const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
          dateFrom = lastMonth.toISOString().split('T')[0];
          dateTo = lastMonthEnd.toISOString().split('T')[0];
          break;
        }
        default:
          break;
      }

      setFilters((prev) => ({
        ...prev,
        dateFrom: { ...prev.dateFrom, value: dateFrom },
        dateTo: { ...prev.dateTo, value: dateTo }
      }));
      setDateRangePreset(preset);
      setPage(1);
    },
    [setFilters, setPage]
  );

  const handleViewModeChange = useCallback((value) => {
    setViewMode(value);
  }, []);

  const handleStatusChange = useCallback(
    (interactionId, newStatusId) => {
      setInteractions((prev) =>
        prev.map((i) =>
          i.id === interactionId
            ? {
                ...i,
                interaction_status_id: newStatusId,
                status_name: userStatuses.find((s) => s.id === newStatusId)?.name || null,
                status_color: userStatuses.find((s) => s.id === newStatusId)?.color || null
              }
            : i
        )
      );
    },
    [userStatuses]
  );

  async function getInteractions() {
    setFilterLoading(true);
    try {
      const queryParams = new URLSearchParams();

      if (filters.q.value) queryParams.set('q', filters.q.value);
      if (filters.company.value) queryParams.set('company', filters.company.value);
      if (filters.contact.value) queryParams.set('contact', filters.contact.value);
      if (filters.company_id.value) queryParams.set('company_id', filters.company_id.value);
      if (filters.company_contact_id.value) queryParams.set('company_contact_id', filters.company_contact_id.value);
      if (filters.type.value) queryParams.set('type', JSON.stringify(filters.type.value));

      // "My Interactions" tab (listTab === 0): filter by current user
      // "All Interactions" tab (listTab === 1): use the user_id filter if set
      if (listTab === 0 && currentUser?.id) {
        queryParams.set('user_id', JSON.stringify([currentUser.id]));
      } else if (filters.user_id.value) {
        queryParams.set('user_id', JSON.stringify(filters.user_id.value));
      }

      if (filters.dateFrom.value) queryParams.set('dateFrom', filters.dateFrom.value);
      if (filters.dateTo.value) queryParams.set('dateTo', filters.dateTo.value);
      if (followUpFilter) queryParams.set('follow_up_filter', followUpFilter);
      if (page > 1) queryParams.set('page', page);
      queryParams.set('limit', '50');

      const data = await getRequest(`/api/interactions?${queryParams.toString()}`);
      setInteractions(data.interactions);
      setTotalCount(data.total);
    } catch (error) {
      console.error('Error fetching interactions:', error);
    }
    setFilterLoading(false);
    setIsTabSwitching(false);
  }

  // Initial load only
  useEffect(() => {
    const fetchData = async () => {
      await getInteractions();
      setIsLoading(false);
    };
    fetchData();
  }, []);

  // Fetch on filter/page/tab changes (without full page loading)
  useEffect(() => {
    if (!isLoading) {
      getInteractions();
    }
  }, [page, filters, listTab, followUpFilter]);

  const getInteractionBadgeColor = (type) => {
    for (const group of Object.values(interactionTypeGroups)) {
      if (group.types.includes(type)) {
        return group.color;
      }
    }
    return 'default';
  };

  const rows = (interactions || []).map((interaction) => {
    // Count attached documents
    let docCount = 0;
    if (interaction.S3ObjectKeys) {
      try {
        const keys = JSON.parse(interaction.S3ObjectKeys);
        docCount = Array.isArray(keys) ? keys.length : 0;
      } catch (e) {
        // Invalid JSON, ignore
      }
    }

    // Check follow-up status
    const today = new Date().toISOString().split('T')[0];
    const followUpDate = interaction.follow_up_date ? String(interaction.follow_up_date).split('T')[0] : null;
    const isOverdue = followUpDate && followUpDate < today;
    const isDueToday = followUpDate && followUpDate === today;

    let projectCell;
    if (interaction.project_id && interaction.project_number) {
      const projectDisplay = interaction.project_description ? `${interaction.project_number}: ${interaction.project_description}` : String(interaction.project_number);
      const truncatedProject = projectDisplay.length > 30 ? `${projectDisplay.slice(0, 30)}...` : projectDisplay;
      projectCell = (
        <Link url={`/projects/${interaction.project_id}`} removeUnderline>
          <Text as="span">{truncatedProject}</Text>
        </Link>
      );
    } else {
      projectCell = (
        <Text as="span" tone="subdued">
          -
        </Text>
      );
    }

    return [
      <Link url={`/interactions/${interaction.id}`}>{interaction.id}</Link>,
      String(interaction.event_date || '')
        .replace('T', ' ')
        .slice(0, 16),
      <Badge tone={getInteractionBadgeColor(interaction.type)}>{interaction.type || 'Unknown'}</Badge>,
      <StatusDropdown interaction={interaction} statuses={userStatuses} onStatusChange={handleStatusChange} />,
      followUpDate ? (
        <Text as="span" tone={isOverdue ? 'critical' : isDueToday ? 'caution' : 'subdued'}>
          {isOverdue ? '● ' : isDueToday ? '○ ' : ''}
          {followUpDate}
        </Text>
      ) : (
        <Text as="span" tone="subdued">
          -
        </Text>
      ),
      interaction.company_id ? <Link url={`/companies/${interaction.company_id}`}>{interaction.NAAM || '-'}</Link> : '-',
      interaction.contact_id ? <Link url={`/contacts/${interaction.contact_id}`}>{interaction.contact_name || '-'}</Link> : '-',
      <InlineStack gap="200" blockAlign="center">
        <Link url={`/interactions/${interaction.id}`}>{interaction.title?.length > 75 ? `${interaction.title.slice(0, 75)}...` : interaction.title || 'No title'}</Link>
        {docCount > 0 && <Badge tone="success">📎 {docCount}</Badge>}
      </InlineStack>,
      projectCell,
      users.find((user) => user.GebruikerID === interaction.user_id)?.Afkorting || 'N/A',
      <ButtonGroup>
        <Button
          size="slim"
          onClick={() => {
            setEditingInteraction(interaction);
            setModalTab(0);
            setIsModalOpen(true);
          }}
        >
          Edit
        </Button>
        <Button
          size="slim"
          onClick={() => {
            navigate(`/interactions/${interaction.id}`);
          }}
        >
          View
        </Button>
      </ButtonGroup>
    ];
  });

  if (isLoading) {
    return (
      <Page title="Interactions">
        <Card>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>loading...</div>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      fullWidth
      title="Interactions"
      primaryAction={{
        content: 'New interaction',
        onAction: () => {
          setEditingInteraction(null);
          setIsModalOpen(true);
        }
      }}
    >
      <Card>
        <Tabs
          fitted
          tabs={[
            { id: 'my-interactions', content: 'My Interactions', panelID: 'my-interactions-panel' },
            { id: 'all-interactions', content: 'All Interactions', panelID: 'all-interactions-panel' }
          ]}
          selected={listTab}
          onSelect={handleListTabChange}
        />

        {/* View toggle, filters, and quick actions - only show on My Interactions tab */}
        {listTab === 0 && (
          <Box padding="300" borderBlockEndWidth="025" borderColor="border">
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="300" blockAlign="center">
                  <ButtonGroup variant="segmented">
                    <Button pressed={viewMode === 'table'} onClick={() => handleViewModeChange('table')} icon={ListBulletedIcon}>
                      Table
                    </Button>
                    <Button pressed={viewMode === 'kanban'} onClick={() => handleViewModeChange('kanban')} icon={ViewIcon}>
                      Kanban
                    </Button>
                  </ButtonGroup>

                  {/* Quick Log Buttons */}
                  <Popover
                    active={quickLogPopoverActive}
                    activator={
                      <Button onClick={() => setQuickLogPopoverActive(true)} disclosure>
                        Quick Log
                      </Button>
                    }
                    onClose={() => setQuickLogPopoverActive(false)}
                    autofocusTarget="first-node"
                  >
                    <ActionList
                      actionRole="menuitem"
                      items={[
                        {
                          content: 'Quick Call',
                          icon: PhoneIcon,
                          onAction: () => {
                            setQuickLogPopoverActive(false);
                            setEditingInteraction({ type: 'outgoing_call', title: '' });
                            setIsModalOpen(true);
                          }
                        },
                        {
                          content: 'Quick Email',
                          icon: EmailIcon,
                          onAction: () => {
                            setQuickLogPopoverActive(false);
                            setEditingInteraction({ type: 'email', title: '' });
                            setIsModalOpen(true);
                          }
                        },
                        {
                          content: 'Quick Meeting',
                          icon: CalendarIcon,
                          onAction: () => {
                            setQuickLogPopoverActive(false);
                            setEditingInteraction({ type: 'appointment_extern', title: '' });
                            setIsModalOpen(true);
                          }
                        }
                      ]}
                    />
                  </Popover>
                </InlineStack>

                <InlineStack gap="200">
                  <Button onClick={() => setStatusModalOpen(true)} variant="plain">
                    Manage Statuses
                  </Button>
                  {window.location.hostname === 'localhost' && (
                    <Button onClick={handleSendFollowUpReminders} loading={sendingReminders} variant="primary" tone="success">
                      Send Follow-up Emails
                    </Button>
                  )}
                </InlineStack>
              </InlineStack>

              {/* Reminder result banner */}
              {reminderBanner && (
                <Banner tone={reminderBanner.tone} onDismiss={() => setReminderBanner(null)}>
                  {reminderBanner.message}
                </Banner>
              )}

              {/* Filter row */}
              <InlineStack gap="300" blockAlign="center">
                {/* Follow-up filter */}
                <div style={{ minWidth: '150px' }}>
                  <Select
                    label="Follow-up"
                    labelHidden
                    options={[
                      { label: 'All follow-ups', value: '' },
                      { label: '● Overdue', value: 'overdue' },
                      { label: '○ Due today', value: 'due_today' },
                      { label: 'Upcoming', value: 'upcoming' },
                      { label: 'Has follow-up', value: 'has_follow_up' }
                    ]}
                    value={followUpFilter}
                    onChange={(value) => {
                      setFollowUpFilter(value);
                      setPage(1);
                    }}
                  />
                </div>

                {/* Date range preset */}
                <div style={{ minWidth: '150px' }}>
                  <Select
                    label="Date range"
                    labelHidden
                    options={[
                      { label: 'All time', value: '' },
                      { label: 'Today', value: 'today' },
                      { label: 'Yesterday', value: 'yesterday' },
                      { label: 'Last 7 days', value: 'last_7_days' },
                      { label: 'Last 30 days', value: 'last_30_days' },
                      { label: 'This month', value: 'this_month' },
                      { label: 'Last month', value: 'last_month' }
                    ]}
                    value={dateRangePreset}
                    onChange={(value) => {
                      if (value === '') {
                        setFilters((prev) => ({
                          ...prev,
                          dateFrom: { ...prev.dateFrom, value: '' },
                          dateTo: { ...prev.dateTo, value: '' }
                        }));
                        setDateRangePreset('');
                        setPage(1);
                      } else {
                        applyDateRangePreset(value);
                      }
                    }}
                  />
                </div>

                {/* Show active filter count */}
                {(followUpFilter || dateRangePreset) && (
                  <Button
                    variant="plain"
                    onClick={() => {
                      setFollowUpFilter('');
                      setDateRangePreset('');
                      setFilters((prev) => ({
                        ...prev,
                        dateFrom: { ...prev.dateFrom, value: '' },
                        dateTo: { ...prev.dateTo, value: '' }
                      }));
                      setPage(1);
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </InlineStack>
            </BlockStack>
          </Box>
        )}

        <Filters
          queryValue={filters.q.value}
          filters={addFilters(filters, setFilters, navigate, setPage)}
          appliedFilters={applyFilters(filters, setFilters, navigate, setPage)}
          loading={false}
          onQueryChange={(e) => handleFilterChange(e, filters, setFilters, 'q', navigate, setPage)}
          onQueryClear={() => removeFilter('q', filters, setFilters, navigate, setPage)}
          autoComplete="off"
          queryPlaceholder="Filter interactions by company, contact or notes"
          onClearAll={() => handleFiltersClearAll(filters, setFilters, navigate, setPage)}
        >
          <ExcelExporterModal filters={filters} setFilters={setFilters} page={page} setPage={setPage} />
        </Filters>
        <div style={{ minHeight: '500px', position: 'relative' }}>
          {isTabSwitching && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.8)', zIndex: 1 }}>Loading...</div>
          )}

          {/* Show Kanban view only on My Interactions tab when viewMode is 'kanban' */}
          {listTab === 0 && viewMode === 'kanban' ? (
            <InteractionKanban
              statuses={userStatuses}
              interactions={interactions}
              onInteractionClick={(interaction) => {
                setEditingInteraction(interaction);
                setModalTab(0);
                setIsModalOpen(true);
              }}
              onInteractionsChange={getInteractions}
            />
          ) : (
            <DataTable
              stickyHeader
              increasedTableDensity
              verticalAlign="middle"
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={['ID', 'Date', 'Type', 'Status', 'Follow-up', 'Company', 'Contact', 'Title', 'Project', 'User', 'Actions']}
              rows={rows}
              footerContent={
                <div>
                  <div style={{ marginBottom: '10px' }}>
                    Displaying {rows.length} of {totalCount} interactions
                  </div>
                  <BlockStack inlineAlign="center">
                    <Pagination
                      hasPrevious={page > 1}
                      onPrevious={() => {
                        if (page > 1) {
                          setPage(page - 1);
                        }
                      }}
                      hasNext={page * 50 < totalCount}
                      onNext={() => {
                        setPage(page + 1);
                      }}
                    />
                  </BlockStack>
                </div>
              }
            />
          )}
        </div>
      </Card>

      {/* Status Management Modal */}
      <StatusManagementModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onStatusesChange={async () => {
          // Refresh statuses
          const data = await getRequest('/api/interaction-statuses');
          setUserStatuses(data);
        }}
      />

      <Modal
        open={isModalOpen}
        onClose={() => {
          // This will only be triggered by the close button, not by clicking outside
          setIsModalOpen(false);
          setEditingInteraction(null);
          setModalTab(0);
        }}
        title={editingInteraction ? `Edit Interaction #${editingInteraction.id}` : 'New Interaction'}
        size="large"
      >
        <Modal.Section>
          {editingInteraction && editingInteraction.id ? (
            <>
              {/* Show banner if documents are attached */}
              {editingInteraction.S3ObjectKeys &&
                (() => {
                  try {
                    const keys = JSON.parse(editingInteraction.S3ObjectKeys);
                    const docCount = Array.isArray(keys) ? keys.length : 0;
                    if (docCount > 0) {
                      return (
                        <Box paddingBlockEnd="400">
                          <Banner tone="info">
                            <InlineStack gap="200" align="space-between" blockAlign="center">
                              <Text as="span">
                                {docCount} {docCount === 1 ? 'document' : 'documents'} attached to this interaction
                              </Text>
                              {modalTab === 0 && (
                                <Button size="slim" onClick={() => setModalTab(1)}>
                                  View documents
                                </Button>
                              )}
                            </InlineStack>
                          </Banner>
                        </Box>
                      );
                    }
                  } catch (e) {
                    // Invalid JSON, ignore
                  }
                  return null;
                })()}

              <Tabs
                tabs={[
                  { id: 'details', content: 'Details', panelID: 'details-panel' },
                  { id: 'documents', content: 'Documents', panelID: 'documents-panel' }
                ]}
                selected={modalTab}
                onSelect={handleModalTabChange}
              >
                {modalTab === 0 && (
                  <div id="details-panel" role="tabpanel">
                    <InteractionForm
                      interaction={editingInteraction}
                      onSubmit={() => {
                        setIsModalOpen(false);
                        setEditingInteraction(null);
                        setModalTab(0);
                        getInteractions();
                      }}
                    />
                  </div>
                )}
                {modalTab === 1 && (
                  <div id="documents-panel" role="tabpanel">
                    <InteractionDocuments interactionId={editingInteraction.id} />
                  </div>
                )}
              </Tabs>
            </>
          ) : (
            <InteractionForm
              interaction={editingInteraction}
              isNew={true}
              onSubmit={() => {
                setIsModalOpen(false);
                setEditingInteraction(null);
                setModalTab(0);
                getInteractions();
              }}
            />
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
